/**
 * Custom HTML element representing an email popup modal.
 *
 * Features:
 * - Delayed popup display based on `data-popup-delay` attribute (in seconds).
 * - Optionally shows only once per user using `data-show-only-once="true"`.
 * - Handles email input validation and enables/disables submit button accordingly.
 * - Provides open and close functionality, with event bindings for close buttons and input changes.
 *
 * Attributes:
 * @attribute {string} data-popup-delay - Delay in seconds before the popup opens (default: 3).
 * @attribute {string} data-show-only-once - If "true", popup will only show once per user.
 *
 * Example usage:
 * <email-popup data-popup-delay="5" data-show-only-once="true"></email-popup>
 *
 * @class
 * @extends HTMLElement
 */
class EmailPopup extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.init();
  }

  init() {
    this.cacheElements();
    this.getData();
    this.bindEvents();
    setTimeout(() => {
      this.handleOpen();
    }, this.popupDelay);
  }

  cacheElements() {
    this.closePopup = this.querySelectorAll('[data-action="close-popup"]');
    this.emailInput = this.querySelector('input[type="email"]');
    this.submitButton = this.querySelector('button[type="submit"]');
  }

  getData() {
    this.popupDelay = this.dataset.popupDelay
      ? parseInt(this.dataset.popupDelay) * 1000
      : 3000;
    this.showOnlyOnce = this.dataset.showOnlyOnce === "true";
  }

  bindEvents() {
    this.closePopup.forEach((button) => {
      button.addEventListener("click", () => this.close());
    });
    // this.emailInput.addEventListener('change', (e) => this.handleChange(e));
  }

  open() {
    this.classList.add("open");
  }

  close() {
    this.classList.remove("open");

    if (this.showOnlyOnce) {
      localStorage.setItem("emailPopupShown_V1", "true");
    }
  }

  handleOpen() {
    console.log(
      "Opening email popup...",
      this.showOnlyOnce,
      localStorage.getItem("emailPopupShown_V1")
    );

    if (
      this.showOnlyOnce &&
      localStorage.getItem("emailPopupShown_V1") === "true"
    ) {
      console.log("Email popup already shown, not opening again.");
      return;
    }

    this.open();
  }

  handleChange(e) {
    const email = e.target.value;
    if (this.validateEmail(email)) {
      this.submitButton.disabled = false;
    } else {
      this.submitButton.disabled = true;
      this.emailInput.setCustomValidity("Please enter a valid email address.");
      this.emailInput.reportValidity();
    }
  }

  handleSubmit(e) {
    if (this.showOnlyOnce) {
      localStorage.setItem("emailPopupShown_V1", "true");
    }
  }

  validateEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  }

  disconnectedCallback() {
    this.closeButton.removeEventListener("click", () => this.close());
    this.submitButton.removeEventListener("click", () => this.submitEmail());
  }
}

if (!customElements.get("email-popup")) {
  customElements.define("email-popup", EmailPopup);
}
